import axios from "axios";
// import { ToastContainer, toast } from 'react-toastify';
import { LocalStorageKeys, LocalStorageService } from "./LocalStorageService";

import { GLOBAL_URL } from "./constants";

const httpClient = axios.create({
  baseURL: `${GLOBAL_URL}/`,
});

// Flag to track if user is viewing 1K Dashboard
let is1kViewEnabledFlag = false;

/**
 * Set the is1kViewEnabled flag for API requests
 * @param {boolean} enabled - Whether the 1K view is enabled
 */
export function setIs1kViewEnabled(enabled) {
  is1kViewEnabledFlag = enabled;
}

/**
 * Get the current is1kViewEnabled flag value
 * @returns {boolean} - The current flag value
 */
export function getIs1kViewEnabled() {
  return is1kViewEnabledFlag;
}

// Axios request interceptor to attach is1kViewEnabled flag to all requests
httpClient.interceptors.request.use(
  (config) => {
    if (is1kViewEnabledFlag) {
      // Add flag as a query parameter
      config.params = {
        ...config.params,
        is1kViewEnabled: true,
      };
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export function setDefaultHeader(header, value) {
  httpClient.defaults.headers.common[header] = value;
}

export async function apiCall(
  method,
  url,
  data,
  header = { "Content-Type": "application/json" }
) {
  try {
    const response = await httpClient({
      method,
      url,
      data,
      headers: header,
      withCredentials: false,
    });
    if (response.status === 200) {
      return response;
    }
    if (response.status === 201) {
      return response;
    }
    if (response.status === 400) {
      return response;
    }
  } catch (error) {
    console.log(error, "error");
    if (error.response) {
      if (error.response.status === 401) {
        // toast.error(error.response, {
        //     position: toast.POSITION.TOP_RIGHT
        // });
        return error.response;
      } else if (error.response.status === 500) {
        // toast.error(error?.response?.data?.title, {  
        //     position: toast.POSITION.TOP_RIGHT
        // });
      } else if (error.response.status === 400) {
        // toast.error(error?.response?.data?.message, {
        //     position: toast.POSITION.TOP_RIGHT
        // });
        return error.response;
      } else if (error.response.status === 403) {
        LocalStorageService.remove(LocalStorageKeys.USER_TOKEN);
        LocalStorageService.remove(LocalStorageKeys.SEARCH_DATA);
        window.location.assign(`${process.env.PUBLIC_URL}/login`);
      }
    } else if (error.request) {
      // toast.error(error.request, {
      //     position: toast.POSITION.TOP_RIGHT
      // });
      // console.log('Error request : ', error.request);
    } else {
      // console.log('Error message : ', error.message);
    }
    // console.log("Error config", error.config);
    // console.log("errorresponse", error.response);
    console.log("Error", error);
    // toast.error(error.request, {
    //     position: toast.POSITION.TOP_RIGHT
    // });
    return false;
  }
}

/**
 * A robust and reusable API client utility for making HTTP requests.
 * It handles URL construction, custom headers, JSON parsing, and robust error handling.
 *
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT', 'DELETE').
 * @param {string} apiUrl - The base URL of the API endpoint.
 * @param {Object<string, string>} [queryParams] - Optional URL query parameters (e.g., { id: '123' }).
 * @param {Object} [params] - Optional parameters for fetch configuration (e.g., credentials, mode).
 * @param {Object<string, string>} [params.headers] - Custom headers to merge with the default 'Content-Type'.
 * @param {any} [body] - The request body (will be automatically JSON stringified if provided).
 * @returns {Promise<any>} The parsed JSON response data.
 * @throws {Error} Throws an error if the request fails (network error or non-2xx status).
 */
export async function apiClient(
  method,
  apiUrl,
  queryParams = {},
  params = {},
  body = null
) {
  // 1. Construct URL with query parameters
  const url = new URL(`${GLOBAL_URL}${apiUrl}`);
  const token = LocalStorageService.get(LocalStorageKeys.USER_TOKEN);
  if (queryParams) {
    Object.keys(queryParams).forEach((key) =>
      url.searchParams.append(key, queryParams[key])
    );
  }

  if (is1kViewEnabledFlag) {
    url.searchParams.append("is1kViewEnabled", true);
  }
  const fullUrl = url.toString();

  // 2. Define default headers and merge custom headers
  const defaultHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  // Merge default headers with user-provided headers, prioritizing user headers on conflict
  const finalHeaders = {
    ...defaultHeaders,
    ...(params.headers || {}),
  };

  // 3. Construct fetch configuration
  const config = {
    method,
    ...params,
    headers: finalHeaders,
  };

  // Add body only if provided and method is not GET/HEAD
  if (
    body !== null &&
    method.toUpperCase() !== "GET" &&
    method.toUpperCase() !== "HEAD"
  ) {
    // Note: For 'application/json' content type, we stringify the body.
    if (finalHeaders["Content-Type"] === "application/json") {
      config.body = JSON.stringify(body);
    } else {
      config.body = body; // For FormData, Blob, etc.
    }
  }

  try {
    const response = await fetch(fullUrl, config);

    // 4. Handle HTTP Errors (Non-2xx status)
    if (!response.ok) {
      let errorDetails = `HTTP Error ${response.status}: ${response.statusText}`;

      // Attempt to read error message from the response body if it exists
      try {
        const errorJson = await response.json();
        errorDetails = errorJson.message || JSON.stringify(errorJson);
      } catch (e) {
        // Ignore parsing error, use default status message
      }

      // Throw a custom error object containing relevant info
      throw new Error(
        `API Request Failed for ${method} ${apiUrl}. Details: ${errorDetails}`
      );
    }

    // 5. Handle Successful Response (2xx)
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      // If JSON, parse and return
      return await response.json();
    }

    // Handle No Content (204)
    if (response.status === 204) {
      return null;
    }

    // Fallback for non-JSON content types (e.g., text, html)
    return await response.text();
  } catch (error) {
    // Throw the error immediately upon network or other failure
    throw error;
  }
}
